import type { ScheduledDashboardData } from '../../../lib/api/scheduled-dashboard';

export const labelClass =
  'text-[10px] text-slate-900 font-medium uppercase tracking-wide leading-tight';
export const footClass = 'text-[10px] text-slate-900 mt-1';

export interface KPICellProps {
  label: string;
  value: React.ReactNode;
  foot?: string;
  /** When provided, renders the cell as a clickable button (drilldown trigger). */
  onClick?: () => void;
  /** When ``onClick`` is set but this is true, the cell renders as a static
   *  (non-clickable) cell. Lets callers note in code that a tile is
   *  intentionally non-drillable for now. */
  disabled?: boolean;
}

export function KPICell({ label, value, foot, onClick, disabled }: KPICellProps) {
  const inner = (
    <>
      <p className={labelClass}>{label}</p>
      <div className="flex items-center justify-center min-h-[44px] w-full">{value}</div>
      {foot && <p className={footClass}>{foot}</p>}
    </>
  );
  if (onClick && !disabled) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col items-center text-center gap-1 rounded-md p-1 -m-1 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-colors cursor-pointer"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="flex flex-col items-center text-center gap-1">
      {inner}
    </div>
  );
}

export function Placeholder({ note = '—' }: { note?: string }) {
  return <span className="text-base font-semibold text-muted-foreground/60">{note}</span>;
}

/** Filter scope threaded from the dashboard shell. Every drilldown / export
 *  call inside a period sub-tab MUST send these filters so the modal's row
 *  count lines up with the tile the user clicked. */
export interface DashboardFilterScope {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
  erp_order_type: '1' | '4';
}

export interface PeriodContentProps {
  data: ScheduledDashboardData;
  formatCurrency: (n: number) => string;
  /**
   * Filter context propagated from the dashboard shell so per-period
   * sub-components can build matching drilldown / export queries.
   */
  filters?: DashboardFilterScope;
}

export function SectionFrame({
  title, accent, subtitle, children,
}: {
  title: string;
  accent: 'red' | 'green' | 'blue';
  subtitle?: string;
  children: React.ReactNode;
}) {
  const ring = accent === 'red'
    ? 'border-red-200 bg-red-50/30'
    : accent === 'green'
      ? 'border-green-200 bg-green-50/30'
      : 'border-blue-200 bg-blue-50/30';
  const titleColor = accent === 'red'
    ? 'text-red-600'
    : accent === 'green'
      ? 'text-green-600'
      : 'text-blue-600';
  return (
    <div className={`border ${ring} rounded-lg p-3 flex flex-col`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className={`font-semibold ${titleColor} text-xs uppercase tracking-wide`}>{title}</h4>
        {subtitle && <span className="text-[10px] text-slate-900">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

// Stub trend data — same shape backend will return so swapping is mechanical.
export function makeStubTrend(label: string, points: number, base = 0): Array<{ x: string; y: number }> {
  void label;
  return Array.from({ length: points }, (_, i) => ({ x: `W${i + 1}`, y: base }));
}

/** Tier a 0-100 percentage to a colour relative to a target. */
export function performanceColor(pct: number, target = 95): string {
  if (pct >= target) return '#0f766e';        // teal — at/above target
  if (pct >= target - 15) return '#d97706';   // amber — within 15pp of target
  return '#dc2626';                            // red — well below target
}

/** Drop a sentinel-zero series into ``null`` so charts render breaks where
 *  there genuinely was no data, rather than a misleading 0% point. */
export function nullIfNoData<T extends { total_with_status: number; otif_pct: number; otd_pct: number }>(
  row: T,
): { otif: number | null; otd: number | null } {
  if (!row.total_with_status) return { otif: null, otd: null };
  return { otif: row.otif_pct, otd: row.otd_pct };
}
